import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, Wallet, Users, UserX, Clock, MessageSquare, TrendingUp, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PACKAGES, inr } from "@/data/content";
import { StatusPill } from "@/components/dashboard/kit";

const pkgName = (s) => PACKAGES.find((p) => p.slug === s)?.name || "—";

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="glass rounded-2xl p-5 flex items-start justify-between">
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{label}</p>
      <p className="font-display text-base sm:text-2xl font-bold mt-2 whitespace-nowrap">{value}</p>
    </div>
    <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${accent ? "text-[var(--on-accent)]" : "glass text-[var(--pink)]"}`} style={accent ? { background: "var(--btn-bg)" } : undefined}><Icon size={20} /></div>
  </div>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-sm text-[var(--muted)]">Loading…</p>;
  const maxDay = Math.max(1, ...d.daily_sales.map((x) => x.amount));

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-bold mb-1">Overview</h1>
      <p className="text-sm text-[var(--muted)] mb-6">{d.month_label} · live numbers from your students and payouts.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-income" label="All-time income" value={inr(d.all_time_income)} icon={IndianRupee} accent />
        <StatCard testid="stat-month-income" label="This month" value={inr(d.month_income)} icon={TrendingUp} />
        <StatCard testid="stat-payout-pending" label="Commission due" value={inr(d.payout_pending)} icon={Wallet} />
        <StatCard testid="stat-net" label="Net P&L" value={inr(d.net_pl)} icon={IndianRupee} />
        <StatCard testid="stat-paid" label="Paid users" value={d.paid_users} icon={Users} accent />
        <StatCard testid="stat-unpaid" label="Unpaid users" value={d.unpaid_users} icon={UserX} />
        <StatCard testid="stat-pending" label="Pending approvals" value={d.pending_users} icon={Clock} />
        <StatCard testid="stat-enquiries" label="Leads" value={d.total_enquiries} icon={MessageSquare} />
      </div>

      <div className="grid xl:grid-cols-[1.6fr_1fr] gap-5 mt-6">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5"><h2 className="font-display text-lg font-semibold">Daily sales · {d.month_label}</h2><span className="text-xs text-[var(--muted)]">{d.daily_sales.length} active days</span></div>
          {d.daily_sales.length === 0 ? <p className="text-sm text-[var(--muted)] py-8 text-center">No approved sales this month yet.</p> : (
            <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
              {d.daily_sales.slice().reverse().map((x) => (
                <div key={x.date} data-testid={`sales-day-${x.date}`} className="flex items-center gap-3 text-sm">
                  <span className="w-20 shrink-0 text-xs text-[var(--muted)]">{new Date(x.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                  <div className="flex-1 h-6 rounded-lg bg-[var(--bg-2)] overflow-hidden"><div className="h-full rounded-lg" style={{ width: `${(x.amount / maxDay) * 100}%`, background: "var(--btn-bg)" }} /></div>
                  <span className="w-24 text-right font-semibold">{inr(x.amount)}</span>
                  <span className="w-14 text-right text-xs text-[var(--muted)]">{x.count} sale{x.count > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold mb-5">Sales by package</h2>
          <div className="space-y-3">
            {PACKAGES.map((p) => {
              const row = d.package_split.find((x) => x.package === p.slug) || { count: 0, amount: 0 };
              return (
                <div key={p.slug} className="flex items-center justify-between text-sm border-b last:border-0 border-[var(--surface-border)] pb-3">
                  <div><p className="font-semibold">{p.name}</p><p className="text-xs text-[var(--muted)]">{row.count} students</p></div>
                  <span className="font-bold text-[var(--pink)]">{inr(row.amount)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-4"><h2 className="font-display text-lg font-semibold">Recent signups</h2><Link to="/admin/students" className="text-xs text-[var(--pink)] font-semibold inline-flex items-center gap-1">View all <ArrowUpRight size={13} /></Link></div>
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left text-xs text-[var(--muted)]"><th className="pb-3">Name</th><th>Package</th><th>Joined</th><th>Status</th></tr></thead>
          <tbody>{d.recent_users.map((u) => (
            <tr key={u.email} className="border-t border-[var(--surface-border)]"><td className="py-3 font-semibold">{u.name}<br /><span className="text-xs text-[var(--muted)] break-all">{u.email}</span></td><td className="text-xs">{pkgName(u.package)}</td><td className="text-xs">{new Date(u.created_at).toLocaleDateString("en-IN")}</td><td><StatusPill status={u.status} /></td></tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}
